//
// # Copies of Types
//
// From our other packages, which have other stuff which we can't import here.
// Beware they will diverge! Probably soon!
//

import typia from "typia";
import { Validator } from "CoreUtils";

//
// # `NoaSchema` Types
//
export interface Catalog {
  libraries: Library[];
}

export interface Library {
  name: string;
  cells: Cell[];
}

export interface Cell {
  name: string;
  views: string[];
}

//
// # `ServerUtils` Types
//

export interface ResolvedPath {
  resolved: string; // Resolved path
  input?: string; // Optional input path
  relativeTo?: string; // Optional relative path
}

export type DbSpec = LocalDbSpec | RemoteDbSpec;

export interface DbPaths {
  libdefs?: string; // LibDefs file path
  env?: string; // Environment file path
}

export type LocalDbSpec = DbPaths & {
  kind: "local";
};

export type RemoteDbSpec = {
  kind: "remote";
  server?: { address: string; port: number }; // Remote URL
  id?: string; // Database name

  // Stuff we probably dont want to need to know about the remote, but do, at least for now
  libdefs?: string; // LibDefs file path
  env?: EnvVars;
};

export type EnvVars = Record<string, string | undefined>;

export type Format = "OA" | "JSON" | "PROTO";
export type Schema = "OA" | "ALPHA";

//
// # `GenAlphaModel` Types
//

// ViewKind from GenAlphaModel/src/cell.ts
export type ViewKind = "schematic" | "symbol" | "layout";

//
// # `GenAlphaSchema` PDK Types (v6)
//

// Copied from GenAlphaSchema/src/v6/pdk.ts
export interface PDK {
  id: string;
  name: string;
  foundry: string;
  processNode: number;
  version: string;
  manufacturingGrid: number;
  stacks: Array<string>;
  layers: Array<PDKLayer>;
}

export interface PDKLayer {
  name: string;
  gds: [number, number];
  color?: Color;
  purposes: Array<{
    name: string;
    purpose: number;
  }>;
  category:
    | "metal"
    | "via"
    | "diffusion"
    | "poly"
    | "implant"
    | "mask"
    | "other";
}

export interface Color {
  r: number;
  g: number;
  b: number;
}

//
// # GenAlphaSchema Content Types (v6)
//
// Minimal content type definitions for validation purposes.
// These are intentionally simplified - we only validate the essential structure,
// not the full nested details.
//
// `schemaVersion` is `string` here to track the real schema, which stopped
// declaring a numeric literal when version names became commit hashes. This
// file is a hand-maintained third copy of the schema and has already diverged
// from it in other ways (see Editor/AGENTS.md); nothing loads depot content through
// it. Deleting it in favour of importing the real types is tracked separately.
//

// Minimal Schematic type for validation
// Copied from GenAlphaSchema v6.Schematic
export interface Schematic {
  schemaVersion: string;
  contentKind: "schematic";
  ident: unknown; // We don't validate the full Ident structure
  pdkId?: string; // Optional PDK identifier - PDK-agnostic schematics omit this
  instances: unknown[]; // We don't validate instance details
  pins: unknown[];
  wireSegments: unknown[];
  graphics?: unknown[];
  bbox?: unknown;
  properties?: unknown[];
  symbols?: unknown[];
  symbolImpl?: unknown;
  layoutImpls?: unknown[];
}

// Minimal Layout type for validation
// Copied from GenAlphaSchema v6.Layout
export interface Layout {
  schemaVersion: string;
  contentKind: "layout";
  ident: unknown;
  pdkId: string;
  stackId: string;
  abs: unknown;
  impl?: unknown;
  properties?: unknown[];
  schematicImpl?: unknown;
}

// Minimal Symbol type for validation
// Copied from GenAlphaSchema v6.Symbol
export interface Symbol {
  schemaVersion: string;
  contentKind: "symbol";
  ident: unknown;
  pins: unknown[];
  graphics?: unknown[];
  selectionBoxes?: unknown[];
  exprLabels?: unknown[];
  bbox?: unknown;
  properties?: unknown[];
  schematicImpls?: unknown[];
}

// Content union type
export type Content = Schematic | Layout | Symbol;

// Typia validators
export const SchematicValidator = new Validator(
  typia.createValidate<Schematic>()
);

export const LayoutValidator = new Validator(typia.createValidate<Layout>());

export const SymbolValidator = new Validator(typia.createValidate<Symbol>());

export const ContentValidator = new Validator(typia.createValidate<Content>());

// Helper type guard functions for convenience
export function isSchematic(content: unknown): content is Schematic {
  return SchematicValidator.validate(content).ok;
}

export function isLayout(content: unknown): content is Layout {
  return LayoutValidator.validate(content).ok;
}

export function isSymbol(content: unknown): content is Symbol {
  return SymbolValidator.validate(content).ok;
}
